'use client'

import { useState, useEffect, useRef } from 'react'
import { CheckCircle2, Sparkles, ArrowRight, Star, Award, Shield, Users, Target, Zap } from 'lucide-react'
import { translations } from '@/lib/translations'

export default function AboutSection({ language }) {
  const t = translations[language]
  const [isVisible, setIsVisible] = useState(false)
  const [activeIndex, setActiveIndex] = useState(null)
  const [hoveredFeature, setHoveredFeature] = useState(null)
  const sectionRef = useRef(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(entry.target)
        }
      },
      { threshold: 0.1, rootMargin: '0px' }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  // Track mouse position for parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect()
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height
        })
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  const features = [
    { 
      title: t.about.feature1, 
      desc: t.about.feature1Desc,
      icon: Star,
      color: 'from-yellow-400 to-orange-500',
      stats: '99.9%',
      statLabel: 'Success Rate'
    },
    { 
      title: t.about.feature2, 
      desc: t.about.feature2Desc,
      icon: Shield,
      color: 'from-blue-400 to-indigo-500',
      stats: '24/7',
      statLabel: 'Support'
    },
    { 
      title: t.about.feature3, 
      desc: t.about.feature3Desc,
      icon: Zap,
      color: 'from-purple-400 to-pink-500',
      stats: '10x',
      statLabel: 'Faster Results'
    }
  ]

  const stats = [
    { value: '500+', label: 'Projects Completed', icon: Target },
    { value: '98%', label: 'Client Satisfaction', icon: Users },
    { value: '15+', label: 'Industry Awards', icon: Award }
  ]

  return (
    <section 
      ref={sectionRef}
      id="about" 
      className="relative py-4 sm:py-4 md:py-4 lg:py-6 px-2 sm:px-6 lg:px-6 overflow-hidden bg-gradient-to-b from-background via-background/95 to-background"
    >
      {/* Advanced background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated gradient orbs */}
        <div 
          className="absolute top-0 -left-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"
          style={{
            transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`,
            transition: 'transform 0.3s ease-out'
          }}
        />
        <div 
          className="absolute bottom-0 -right-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse animation-delay-2000"
          style={{
            transform: `translate(${-mousePosition.x * 20}px, ${-mousePosition.y * 20}px)`,
            transition: 'transform 0.3s ease-out'
          }}
        />
        
        {/* Animated grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `
            radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0),
            linear-gradient(45deg, transparent 48%, currentColor 49%, currentColor 51%, transparent 52%)
          `,
          backgroundSize: '50px 50px, 30px 30px',
          animation: 'moveGrid 20s linear infinite'
        }} />

        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-primary/20 rounded-full"
            style={{
              width: Math.random() * 4 + 2 + 'px',
              height: Math.random() * 4 + 2 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animation: `floatParticle ${Math.random() * 10 + 10}s linear infinite`,
              animationDelay: Math.random() * 5 + 's'
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Premium section header */}
        <div className="text-center mb-4 sm:mb-12 md:mb-4 lg:mb-4">
          <div className={`inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-gradient-to-r from-primary/10 via-primary/5 to-secondary/10 backdrop-blur-md border border-primary/20 mb-4 sm:mb-6 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'
          }`}>
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-primary animate-pulse" />
            <span className="text-xs sm:text-sm font-semibold text-primary tracking-wide">About Us</span>
          </div>
          
          <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 transform transition-all duration-700 delay-200 ${
            isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'
          }`}>
            <span className="bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent bg-300% animate-gradient">
              {t.about.title}
            </span>
          </h2>
          
          
        </div>

        {/* Main content grid - Responsive layout */}
        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 items-start">
          {/* Left column - About text with enhanced cards */}
          <div className={`space-y-4 sm:space-y-6 transform transition-all duration-700 delay-400 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          }`}>
            {[1, 2, 3].map((num, index) => (
              <div
                key={index}
                className="group relative animate-float"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary via-primary/50 to-secondary rounded-2xl sm:rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-all duration-500 group-hover:duration-300" />
                <div className="relative bg-gradient-to-br from-card to-card/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-border/50 shadow-lg hover:border-primary/30 hover:shadow-xl transition-all duration-300">
                  {/* Decorative corner accent */}
                  <div className="absolute top-0 right-0 w-12 sm:w-16 h-12 sm:h-16 overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 sm:w-24 h-16 sm:h-24 bg-gradient-to-br from-primary/20 to-secondary/20 rotate-12 transform translate-x-8 -translate-y-8" />
                  </div>
                  
                  <p className="text-sm sm:text-base md:text-lg text-foreground/90 leading-relaxed">
                    {t.about[`description${num}`]}
                  </p>
                  
                  {/* Animated gradient dot */}
                  <div className="absolute bottom-4 right-4 w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full blur-xl group-hover:blur-2xl group-hover:scale-150 transition-all duration-500" />
                </div>
              </div>
            ))}

            
          </div>

          {/* Right column - Premium Features */}
          <div className={`transform transition-all duration-700 delay-600 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}>
            <div className="relative group/container">
              {/* Animated background layers */}
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 via-secondary/30 to-primary/30 rounded-3xl blur-2xl opacity-75 group-hover/container:opacity-100 animate-gradient-xy transition-all duration-700" />
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-secondary rounded-2xl opacity-0 group-hover/container:opacity-20 transition-opacity duration-500" />
              
              {/* Main features container */}
              <div className="relative bg-gradient-to-br from-card via-card/95 to-card/90 backdrop-blur-md rounded-2xl sm:rounded-3xl p-5 sm:p-6 md:p-8 border border-border/50 shadow-2xl overflow-hidden">
                {/* Header with animated icon */}
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-primary/30 rounded-full blur-md animate-ping" />
                    <div className="relative w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Award className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                    Why Choose Us
                  </h3>
                </div>
                
                {/* Features list with advanced hover effects */}
                <div className="space-y-4 sm:space-y-5">
                  {features.map((feature, index) => {
                    const Icon = feature.icon
                    const isHovered = hoveredFeature === index
                    
                    return (
                      <div
                        key={index}
                        className="group/feature relative"
                        
                        onMouseLeave={() => setHoveredFeature(null)}
                      >
                        {/* Animated background with gradient */}
                        <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover/feature:opacity-10 rounded-xl sm:rounded-2xl transition-all duration-500 transform group-hover/feature:scale-105`} />
                        
                        <div className="relative flex flex-col sm:flex-row gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-transparent group-hover/feature:border-primary/20 transition-all duration-300">
                          {/* Icon with advanced animations */}
                          <div className="relative flex-shrink-0">
                            <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-lg blur-md opacity-0 group-hover/feature:opacity-50 transition-all duration-500 transform group-hover/feature:scale-150`} />
                            <div className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br ${feature.color} p-2 sm:p-2.5 flex items-center justify-center transform group-hover/feature:scale-110 group-hover/feature:rotate-3 transition-all duration-300`}>
                              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </div>
                            
                            {/* Ripple effects */}
                            {isHovered && (
                              <>
                                <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
                                <div className="absolute inset-0 rounded-full bg-primary/20 animate-pulse" />
                              </>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                              <h4 className="text-base sm:text-lg font-bold text-foreground truncate">
                                {feature.title}
                              </h4>
                              {feature.stats && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                                  {feature.stats} {feature.statLabel}
                                </span>
                              )}
                            </div>
                            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed line-clamp-2 sm:line-clamp-none">
                              {feature.desc}
                            </p>
                          </div>

                          
                        </div>

                        {/* Animated border gradient */}
                        <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r ${feature.color} group-hover/feature:w-full transition-all duration-500`} />
                      </div>
                    )
                  })}
                </div>

               
              </div>
            </div>
          </div>
        </div>

        {/* Decorative bottom wave */}
        <div className="absolute bottom-0 left-0 right-0 h-16 sm:h-20 md:h-24 pointer-events-none">
          <svg
            className="absolute bottom-0 w-full h-full text-primary/5"
            preserveAspectRatio="none"
            viewBox="0 0 1200 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 0v46.29c47.79 22.2 103.59 32.17 158 28 70.36-5.37 136.33-33.31 206.8-37.5 73.84-4.36 147.54 16.88 218.2 35.26 69.27 18 138.3 24.88 209.4 13.08 36.15-6 69.85-17.84 104.45-29.34C989.49 25 1113-14.29 1200 52.47V0z"
              fill="currentColor"
              opacity="0.4"
            />
            <path
              d="M0 0v15.81c13 21.11 27.64 41.05 47.69 56.24C99.41 111.27 165 111 224.58 91.58c31.15-10.15 60.09-26.07 89.67-39.8 40.92-19 84.73-46 130.83-49.67 36.26-2.85 70.9 9.42 98.6 31.56 31.77 25.39 62.32 62 103.63 73 40.44 10.79 81.35-6.69 119.13-24.28s75.16-39 116.92-43.05c59.73-5.85 113.28 22.88 168.9 38.84 30.2 8.66 59 6.17 87.09-7.5 22.43-10.89 48-26.93 60.65-49.24V0z"
              fill="currentColor"
              opacity="0.2"
            />
          </svg>
        </div>
      </div>

      <style jsx>{`
        @keyframes moveGrid {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(50px) translateY(50px); }
        }
        
        @keyframes floatParticle {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 0.5; }
          90% { opacity: 0.5; }
          100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
        }
        
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .animate-gradient {
          background-size: 300% 300%;
          animation: gradient 8s ease infinite;
        }
        
        .animate-gradient-xy {
          background-size: 400% 400%;
          animation: gradient 15s ease infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .bg-300\% {
          background-size: 300%;
        }
        
        /* Responsive text utilities */
        @media (max-width: 640px) {
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        }
        
        /* Smooth scrolling for anchor links */
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, var(--primary), var(--secondary));
          border-radius: 5px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, var(--primary-dark), var(--secondary-dark));
        }
      `}</style>
    </section>
  )
}